import * as _ from 'underscore';

import { call, select } from 'redux-saga/effects';
import { IStateService } from '../../../../../typings/globals/angular-ui-router/index';
import {
  DatasourceDefinition,
  FlowDefinition,
  ProjectDefinition,
  WorkbookDefinition
} from '../../../../ts/models/ContentDefinitions';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { IExplorableItemTypeInfo } from '../../../../ts/models/resourceInfoParts';
import { DataSources } from '../../../../ts/resources/Datasources';
import { Projects } from '../../../../ts/resources/Projects';
import { Workbooks } from '../../../../ts/resources/Workbooks';
import { selectSpaceInfo } from '../../../selectors/ContentSelectors';
import { ServiceLocator } from '../../../ServiceLocator';
import { SpaceInfo } from '../../../state/SpaceInfo';


/**
 * TODO:
 * As our refreshes get more complicated (depending on where we are, we refresh in different ways),
 * we might want a better pattern to keep things under control (separate the "execute action" portion
 * of the saga from the "refresh things when we're done").
 *
 * For example, it would be nice if we could just put a refresh action and it always refreshes the
 * appropriate scope no matter what action we did on what content. I think we're actually pretty close
 * to that, if we can supply the action with the updated content items, which the saga can then decide
 * to put into the store, or do a hard reload (or redirect to another page, in the case the content was
 * deleted).
 *
 * The obstacle is SetOwner, like many of our actions soon will, supports multiple content items. We
 * don't have a great way of fetching multiple, but specific items, right now.
 */
export function* reloadSpaceIfUpdatingCurrentSpace(contentThatWasUpdated: IExplorableItemTypeInfo[]): IterableIterator<any> {
  const currentSpaceInfo: SpaceInfo = yield select(selectSpaceInfo);

  const isCurrentSpace = (item: IExplorableItemTypeInfo) => {
    return item.contentType === currentSpaceInfo.spaceType
          && item.id === currentSpaceInfo.item.id;
  };

  const isExternalContent = (item: IExplorableItemTypeInfo) => {
    return item.contentType === 'database' || item.contentType === 'table';
  };

  // Always reload if external content is updated as well. We don't know if we're on a place page for external content because the
  // navigation for external content is not in vizportal so the SpaceInfo is not updated. Reload in case we're on a place page
  if (_.some(contentThatWasUpdated, isCurrentSpace) || _.some(contentThatWasUpdated, isExternalContent)) {
    yield call(reloadSpace);
    return true;
  }
  return false;
}

export function reloadSpace() {
  const stateService = ServiceLocator.get<IStateService>('$state');
  stateService.hardReload();
}

export function fetchContent(content: IContentInfo): ng.IPromise<IContentInfo> {
  switch (content.contentType) {
    case DatasourceDefinition.contentType:
      const datasourceResource = ServiceLocator.get<DataSources>('DataSources');
      return datasourceResource.getById(content.id);
    case FlowDefinition.contentType:
      const flowsResource = ServiceLocator.get<Projects>('Flows');
      return flowsResource.getById(content.id);
    case ProjectDefinition.contentType:
      const projectsResource = ServiceLocator.get<Projects>('Projects');
      return projectsResource.getById(content.id);
    case WorkbookDefinition.contentType:
      const workbooksResource = ServiceLocator.get<Workbooks>('Workbooks');
      return workbooksResource.getById(content.id);
  }

  return Q.when<IContentInfo>(content) as ng.IPromise<IContentInfo>;
}

